import { request, type RiskException } from './api'
import type { Tone } from '../components/kit'

// The due diligence exception workflow (section 4 of the product officer's
// observations), as the API serves it: apps/tprm-api exception_workflow.go and
// exception_settings.go. Who may do what is worked out by the API and arrives
// on each exception as may* flags and a refusal sentence, so this console
// holds no copy of the routing or the authority.

export type PersonaRef = { code: string; name: string }

export type TrackStep = { key: string; label: string; state: 'done' | 'current' | 'upcoming' | 'stopped' }

export type ExceptionReview = {
  id: string
  persona: string
  recommendation: 'APPROVE' | 'REJECT'
  note: string
  reviewedBy: string
  reviewedAt: string
}

export type AuthorityStep = {
  index: number
  persona: string
  personaName: string
  capability: string
  why?: string
  signed: boolean
  signedBy?: string
  signedAt?: string
  note?: string
  mayAct: boolean
  refusal?: string
}

export type AuthorityDecision = {
  level: string
  levelName: string
  inOrder: boolean
  versionId: string
  versionNo: number
  round: number
  complete: boolean
  noLevel: boolean
  steps: AuthorityStep[]
  deciders: PersonaRef[]
}

export type ExceptionWorkflow = {
  id: string
  vendorId: string
  vendorName: string
  status: string
  statusName: string
  title: string
  requestedBy: string
  expiresOn: string
  expired: boolean
  findingId?: string
  gateKey?: string
  governed: boolean
  parameterCode?: string
  parameterName?: string
  severity?: string
  severityName?: string
  correctiveAction?: string
  targetClosureOn?: string
  supportingEvidence: string[]
  vendorResponse?: string
  vendorResponseOn?: string
  vendorResponseBy?: string
  reviewPersona?: string
  reviewerName?: string
  // The function deciding in the risk officer's place, pinned when the
  // exception was routed (release 3.1: the compliance officer, for legal,
  // regulatory and compliance gaps). Absent for the authority as it stands.
  decidePersona?: string
  routedBy?: string
  routedAt?: string
  authorityLevel?: string
  levelName?: string
  escalatedFrom?: string
  decisionRound: number
  decisionReason?: string
  decidedBy?: string
  decidedAt?: string
  closureEvidence: string[]
  closureNote?: string
  closedBy?: string
  closedAt?: string
  review?: ExceptionReview | null
  track: TrackStep[]
  waitingOn: PersonaRef[]
  waitingForMe: boolean
  closureOverdue: boolean
  decision?: AuthorityDecision | null
  mayClarify: boolean
  mayRoute: boolean
  mayReview: boolean
  mayDecide: boolean
  mayEscalate: boolean
  mayClose: boolean
  refusal?: string
  nextStep: string
}

// A row of the register, with the workflow for one raised under it.
export type DDException = RiskException & { statusName?: string; workflow?: ExceptionWorkflow | null }

export type Severity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'
export const SEVERITIES: { code: Severity; name: string }[] = [
  { code: 'LOW', name: 'Low' },
  { code: 'MEDIUM', name: 'Medium' },
  { code: 'HIGH', name: 'High' },
  { code: 'CRITICAL', name: 'Critical' },
]

// The tone a severity is shown in: the kit's, so both themes are right.
export function severityTone(code?: string): Tone {
  switch (code) {
    case 'CRITICAL':
      return 'crit'
    case 'HIGH':
      return 'warn'
    case 'MEDIUM':
      return 'info'
  }
  return 'neutral'
}

export type DDParameter = {
  code: string
  name: string
  riskTypes: string[]
  riskTypeNames?: string[]
  reviewerPersona: string
  reviewerName?: string
  // Who decides in the risk officer's place (release 3.1); '' or absent for
  // the authority as it stands.
  deciderPersona?: string
  deciderName?: string
  active: boolean
  displayOrder?: number
  updatedBy?: string
  updatedAt?: string
}

export type DDCatalogue = {
  parameters: DDParameter[]
  reviewers: { code: string; name: string; canReview: boolean; why?: string }[]
  // The functions that may decide in the risk officer's place: whoever can
  // decide a risk position at all.
  deciders?: { code: string; name: string; canDecide: boolean; why?: string }[]
  riskTypes: { code: string; name: string }[]
}

export type AuthorityDocument = {
  levels: { severity: Severity; steps: { persona: string; why?: string }[]; inOrder: boolean }[]
}

export type AuthorityVersion = {
  id: string
  versionNo: number
  status: 'DRAFT' | 'ACTIVE' | 'RETIRED'
  contentSha256?: string
  notes?: string
  createdBy: string
  createdAt: string
  activatedBy?: string
  activatedAt?: string
  authority?: AuthorityDocument
}

export type Decider = { code: string; name: string; canDecide: boolean; why?: string }

export type ExceptionRaise = {
  vendorId: string
  findingId?: string
  gateKey?: string
  title: string
  justification: string
  compensatingControl: string
  ownerUserId: string
  expiresOn: string
  parameterCode: string
  severity: string
  correctiveAction?: string
  targetClosureOn?: string
  vendorResponse?: string
  vendorResponseOn?: string
}

const enc = encodeURIComponent

export const exceptionsApi = {
  list: () => request<{ exceptions: DDException[] }>('/v1/exceptions'),
  // Raising one. With the corrective action and the target closure date the
  // API sends it straight to its reviewing function, and says so in note.
  create: (body: ExceptionRaise) =>
    request<{ id: string; status: string; expiresOn: string; findingId?: string; note?: string; reviewerName?: string }>(
      '/v1/exceptions',
      { method: 'POST', body: JSON.stringify(body) },
    ),
  // What an executive, or any reviewer, is being waited on for.
  waitingForMe: () => request<{ exceptions: ExceptionWorkflow[] }>('/v1/exceptions/waiting-for-me'),
  workflow: (id: string) => request<ExceptionWorkflow>('/v1/exceptions/' + enc(id) + '/workflow'),
  clarify: (
    id: string,
    body: {
      correctiveAction?: string
      targetClosureOn?: string
      supportingEvidence?: string[]
      vendorResponse?: string
      vendorResponseOn?: string
    },
  ) => request<ExceptionWorkflow>('/v1/exceptions/' + enc(id) + '/clarification', { method: 'POST', body: JSON.stringify(body) }),
  route: (id: string) => request<ExceptionWorkflow>('/v1/exceptions/' + enc(id) + '/route', { method: 'POST' }),
  review: (id: string, recommendation: 'APPROVE' | 'REJECT', note: string) =>
    request<ExceptionWorkflow>('/v1/exceptions/' + enc(id) + '/review', {
      method: 'POST',
      body: JSON.stringify({ recommendation, note }),
    }),
  decide: (id: string, decision: 'APPROVE' | 'REJECT' | 'REMEDIATE' | 'ESCALATE', reason: string, stepIndex?: number) =>
    request<{ id: string; status: string; note: string; workflow: ExceptionWorkflow }>('/v1/exceptions/' + enc(id) + '/decide', {
      method: 'POST',
      body: JSON.stringify({ decision, reason, stepIndex }),
    }),
  close: (id: string, closureEvidence: string[], closureNote: string) =>
    request<{ id: string; status: string; note: string; workflow: ExceptionWorkflow }>('/v1/exceptions/' + enc(id) + '/close', {
      method: 'POST',
      body: JSON.stringify({ closureEvidence, closureNote }),
    }),

  catalogue: () => request<DDCatalogue>('/v1/settings/due-diligence-parameters'),
  saveCatalogue: (parameters: DDParameter[]) =>
    request<DDCatalogue>('/v1/settings/due-diligence-parameters', {
      method: 'PUT',
      body: JSON.stringify({
        parameters: parameters.map((p) => ({
          code: p.code,
          name: p.name,
          riskTypes: p.riskTypes,
          reviewerPersona: p.reviewerPersona,
          deciderPersona: p.deciderPersona ?? '',
          active: p.active,
        })),
      }),
    }),

  authorityVersions: () => request<{ versions: AuthorityVersion[]; deciders: Decider[] }>('/v1/exception-authority-versions'),
  draftAuthority: (authority?: AuthorityDocument, notes = '') =>
    request<{ id: string; versionNo: number; status: string }>('/v1/exception-authority-versions', {
      method: 'POST',
      body: JSON.stringify({ authority, notes }),
    }),
  saveAuthorityDraft: (id: string, authority: AuthorityDocument, notes = '') =>
    request<{ id: string; status: string; problems: string[] }>('/v1/exception-authority-versions/' + enc(id), {
      method: 'PUT',
      body: JSON.stringify({ authority, notes }),
    }),
  promoteAuthority: (id: string) =>
    request<{ id: string; versionNo: number; status: string; note: string }>(
      '/v1/exception-authority-versions/' + enc(id) + '/promote',
      { method: 'POST' },
    ),
}

// The words for who is waited on: "the CISO", "the risk officer and the CISO".
export function personasWords(ps: PersonaRef[]): string {
  const words = ps.map((p) => (p.name === p.name.toUpperCase() ? 'the ' + p.name : 'the ' + p.name.toLowerCase()))
  if (words.length === 0) return 'nobody'
  if (words.length === 1) return words[0] ?? 'nobody'
  return words.slice(0, -1).join(', ') + ' and ' + words[words.length - 1]
}

// The names alone, for a column or a label: "Risk officer and CISO".
export function personaNames(ps: PersonaRef[]): string {
  const names = ps.map((p) => p.name)
  if (names.length <= 1) return names[0] ?? '-'
  return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1]
}
